import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, type AudioAsset, type AutoScoreSegment, type ElevenVoice } from "../../api";
import { MicRecorder } from "./MicRecorder";
import { VoicePicker } from "./VoicePicker";
import type { StopResult } from "../../lib/voiceoverRecorder";

type SceneLike = {
  uid: string;
  durationSec: number;
  fromSec?: number;
  inputs?: Record<string, unknown>;
};

type ClipLayout = { scene: { uid: string }; fromFrames: number; durFrames: number };

type ToastFn = (kind: "success" | "error" | "info", message: string) => void;

type Props = {
  fps: number;
  scenes: SceneLike[];
  layout: ClipLayout[];
  totalSec: number;
  playheadSec: number;
  locale?: string;
  /** Text der aktuell ausgewählten Szene — füllt das TTS-Feld vor. */
  selectedSceneText?: string;
  hasVoiceover: boolean;
  // Recording-Hooks (reel-synchron) — identisch zum VoiceoverPanel.
  recordPlayPreview: boolean;
  onRecordPlayPreviewToggle: (v: boolean) => void;
  onPlayPreview: () => void;
  onPausePreview: () => void;
  // Übernahme ins Reel:
  onPlaceVoiceover: (asset: AudioAsset, fromSec: number, durSec: number) => void;
  onSetMusic: (asset: AudioAsset) => void;
  onAddSfx: (asset: AudioAsset, fromSec: number) => void;
  onApplyAutoScore: (
    segments: { sceneUid: string; fromSec: number; durSec: number; src: string; assetId: string; label: string }[],
  ) => void;
  showToast?: ToastFn;
};

/** Liest sichtbaren Text aus den Scene-Inputs für TTS / Auto-Vertonung. */
function extractSceneText(scene: SceneLike): string {
  const inp = (scene.inputs ?? {}) as Record<string, unknown>;
  const parts: string[] = [];
  for (const key of ["text", "subtext", "title", "number", "unit", "cta", "label"]) {
    const v = inp[key];
    if (typeof v === "string" && v.trim()) parts.push(v.trim());
  }
  const items = inp.items;
  if (Array.isArray(items)) {
    for (const it of items) {
      if (typeof it === "string" && it.trim()) {
        // "iconName: text" → nur den Text-Teil nehmen.
        parts.push(it.includes(":") ? it.split(":").slice(1).join(":").trim() : it.trim());
      }
    }
  }
  return parts.join(". ");
}

/** Probet die echte Audiolänge clientseitig (Server hat kein ffprobe). */
function probeDuration(url: string): Promise<number> {
  return new Promise((resolve) => {
    const a = new Audio();
    a.preload = "metadata";
    const finish = (sec: number): void => {
      a.src = "";
      resolve(Number.isFinite(sec) && sec > 0 ? sec : 0);
    };
    a.addEventListener("loadedmetadata", () => finish(a.duration), { once: true });
    a.addEventListener("error", () => finish(0), { once: true });
    setTimeout(() => finish(a.duration || 0), 5000);
    a.src = url;
  });
}

const ResultPreview: React.FC<{ asset: AudioAsset; children?: React.ReactNode }> = ({ asset, children }) => (
  <div className="el-result">
    <audio controls src={asset.url} className="el-result-audio" />
    <div className="el-result-actions">{children}</div>
  </div>
);

export const ElevenLabsPanel: React.FC<Props> = ({
  fps,
  scenes,
  layout,
  totalSec,
  playheadSec,
  locale = "de",
  selectedSceneText,
  hasVoiceover,
  recordPlayPreview,
  onRecordPlayPreviewToggle,
  onPlayPreview,
  onPausePreview,
  onPlaceVoiceover,
  onSetMusic,
  onAddSfx,
  onApplyAutoScore,
  showToast,
}) => {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [own, setOwn] = useState<ElevenVoice[]>([]);
  const [library, setLibrary] = useState<ElevenVoice[]>([]);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [voiceId, setVoiceId] = useState<string | null>(null);
  const [voicesErr, setVoicesErr] = useState<string | null>(null);
  const [loadingVoices, setLoadingVoices] = useState(false);

  // Sekunden-Helfer je Szene.
  const sceneTimings = useMemo(() => {
    const map = new Map<string, { fromSec: number; durSec: number }>();
    for (const c of layout) {
      map.set(c.scene.uid, { fromSec: c.fromFrames / fps, durSec: c.durFrames / fps });
    }
    return map;
  }, [layout, fps]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const s = await api.elevenStatus();
        if (!alive) return;
        setEnabled(s.enabled);
        if (!s.enabled) return;
        setLoadingVoices(true);
        const v = await api.elevenVoices();
        if (!alive) return;
        setOwn(v.own);
        setLibrary(v.library);
        setFavorites(new Set(v.favorites));
        // Default: erste eigene Stimme (Favorit bevorzugt), sonst Bibliothek.
        const fav = [...v.own, ...v.library].find((x) => v.favorites.includes(x.voiceId));
        setVoiceId(fav?.voiceId ?? v.own[0]?.voiceId ?? v.library[0]?.voiceId ?? null);
      } catch (e) {
        if (alive) setVoicesErr((e as Error).message);
      } finally {
        if (alive) setLoadingVoices(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const toggleFavorite = (id: string): void => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      void api.elevenSetFavorites([...next]).catch(() => undefined);
      return next;
    });
  };

  if (enabled === false) {
    return (
      <div className="side-panel el-panel">
        <div className="side-panel-head"><h4>🎧 Vertonung (ElevenLabs)</h4></div>
        <div className="el-disabled">
          <p>ElevenLabs ist nicht konfiguriert.</p>
          <p className="muted" style={{ fontSize: 12 }}>
            Trage <code>ELEVENLABS_API_KEY</code> in die <code>.env</code> ein (siehe
            <code> .env.example</code>) und starte den Server neu. Danach kannst du hier
            Stimmen tauschen, Text vorlesen lassen, Musik und Sound-Effekte erzeugen und das
            ganze Reel automatisch vertonen.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="side-panel el-panel">
      <div className="side-panel-head">
        <h4>🎧 Vertonung (ElevenLabs)</h4>
        <span className="muted" style={{ fontSize: 11 }}>Reel: {totalSec.toFixed(1)}s</span>
      </div>

      {voicesErr && <div className="audio-library-error">{voicesErr}</div>}
      {enabled === null || loadingVoices ? (
        <div className="muted" style={{ fontSize: 12, padding: "8px 0" }}>Lade Stimmen…</div>
      ) : (
        <VoiceSection
          own={own}
          library={library}
          favorites={favorites}
          voiceId={voiceId}
          onChange={setVoiceId}
          onToggleFavorite={toggleFavorite}
        />
      )}

      <VoiceChangerSection
        voiceId={voiceId}
        playheadSec={playheadSec}
        recordPlayPreview={recordPlayPreview}
        onRecordPlayPreviewToggle={onRecordPlayPreviewToggle}
        onPlayPreview={onPlayPreview}
        onPausePreview={onPausePreview}
        hasVoiceover={hasVoiceover}
        onPlaceVoiceover={onPlaceVoiceover}
        showToast={showToast}
      />

      <TtsSection
        voiceId={voiceId}
        playheadSec={playheadSec}
        prefill={selectedSceneText ?? ""}
        onPlaceVoiceover={onPlaceVoiceover}
        showToast={showToast}
      />

      <MusicSection totalSec={totalSec} onSetMusic={onSetMusic} showToast={showToast} />

      <SfxSection playheadSec={playheadSec} onAddSfx={onAddSfx} showToast={showToast} />

      <AutoScoreSection
        voiceId={voiceId}
        locale={locale}
        scenes={scenes}
        sceneTimings={sceneTimings}
        onApplyAutoScore={onApplyAutoScore}
        showToast={showToast}
      />
    </div>
  );
};

// ---------------- Sections ----------------

const VoiceSection: React.FC<{
  own: ElevenVoice[];
  library: ElevenVoice[];
  favorites: Set<string>;
  voiceId: string | null;
  onChange: (id: string) => void;
  onToggleFavorite: (id: string) => void;
}> = (props) => {
  const selName =
    [...props.own, ...props.library].find((v) => v.voiceId === props.voiceId)?.name ?? "—";
  return (
    <details className="el-section" open>
      <summary>🎚 Zielstimme · <strong>{selName}</strong></summary>
      <VoicePicker
        own={props.own}
        library={props.library}
        favorites={props.favorites}
        value={props.voiceId}
        onChange={props.onChange}
        onToggleFavorite={props.onToggleFavorite}
      />
    </details>
  );
};

const VoiceChangerSection: React.FC<{
  voiceId: string | null;
  playheadSec: number;
  recordPlayPreview: boolean;
  onRecordPlayPreviewToggle: (v: boolean) => void;
  onPlayPreview: () => void;
  onPausePreview: () => void;
  hasVoiceover: boolean;
  onPlaceVoiceover: (asset: AudioAsset, fromSec: number, durSec: number) => void;
  showToast?: ToastFn;
}> = ({
  voiceId,
  playheadSec,
  recordPlayPreview,
  onRecordPlayPreviewToggle,
  onPlayPreview,
  onPausePreview,
  hasVoiceover,
  onPlaceVoiceover,
  showToast,
}) => {
  const [recorded, setRecorded] = useState<{ blob: Blob; mime: string; durSec: number; startSec: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ asset: AudioAsset; startSec: number; durSec: number } | null>(null);

  const onRecordingFinished = (res: StopResult): void => {
    setResult(null);
    setRecorded({ blob: res.rawBlob, mime: res.rawMime, durSec: res.durationSec, startSec: res.playerStartSec });
  };

  const convert = async (): Promise<void> => {
    if (!recorded || !voiceId || busy) return;
    setBusy(true);
    try {
      const ext = recorded.mime.includes("mp4") ? "mp4" : recorded.mime.includes("ogg") ? "ogg" : "webm";
      const r = await api.elevenVoiceChanger(recorded.blob, voiceId, { filename: `take.${ext}`, name: "Voice-Changer" });
      const dur = (await probeDuration(r.asset.url)) || recorded.durSec;
      setResult({ asset: r.asset, startSec: recorded.startSec, durSec: dur });
      showToast?.("success", "Stimme umgewandelt — anhören & übernehmen.");
    } catch (e) {
      showToast?.("error", `Voice-Changer fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="el-section">
      <summary>🎙️ Aufnehmen & Stimme tauschen</summary>
      <p className="muted el-hint">
        Nimm dein Reel-Voiceover mit dem Mikrofon auf (das Reel läuft synchron mit), dann
        legt ElevenLabs deine Zielstimme darüber. Maus/Stimme bleiben dein Timing.
      </p>
      <MicRecorder
        playheadSec={playheadSec}
        autoPlayWhilstRecording={recordPlayPreview}
        onAutoPlayToggle={onRecordPlayPreviewToggle}
        onPlayPreview={onPlayPreview}
        onPausePreview={onPausePreview}
        onRecordingFinished={onRecordingFinished}
        hasExistingVoiceover={hasVoiceover}
      />
      {recorded && (
        <div className="el-recorded">
          <span className="muted" style={{ fontSize: 12 }}>
            Aufnahme: {recorded.durSec.toFixed(1)}s · startet bei {recorded.startSec.toFixed(1)}s
          </span>
          <button
            type="button"
            className="btn primary sm"
            disabled={!voiceId || busy}
            onClick={() => void convert()}
            title={voiceId ? "Zielstimme drüberlegen" : "Erst eine Zielstimme wählen"}
          >
            {busy ? "Wandle um…" : "🎚 Stimme drauflegen"}
          </button>
        </div>
      )}
      {result && (
        <ResultPreview asset={result.asset}>
          <button
            type="button"
            className="btn primary sm"
            onClick={() => {
              onPlaceVoiceover(result.asset, Math.max(0, result.startSec), result.durSec);
              showToast?.("success", "Als Voiceover übernommen.");
            }}
          >
            ✓ Als Voiceover übernehmen
          </button>
        </ResultPreview>
      )}
    </details>
  );
};

const TtsSection: React.FC<{
  voiceId: string | null;
  playheadSec: number;
  prefill: string;
  onPlaceVoiceover: (asset: AudioAsset, fromSec: number, durSec: number) => void;
  showToast?: ToastFn;
}> = ({ voiceId, playheadSec, prefill, onPlaceVoiceover, showToast }) => {
  const [text, setText] = useState(prefill);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ asset: AudioAsset; durSec: number } | null>(null);
  const lastPrefill = useRef(prefill);
  useEffect(() => {
    // Übernimm neuen Szenentext nur, wenn das Feld noch unverändert war.
    if (prefill !== lastPrefill.current && (text === "" || text === lastPrefill.current)) {
      setText(prefill);
    }
    lastPrefill.current = prefill;
  }, [prefill]);

  const generate = async (): Promise<void> => {
    if (!text.trim() || !voiceId || busy) return;
    setBusy(true);
    try {
      const r = await api.elevenTts({ text: text.trim(), voiceId });
      const dur = (await probeDuration(r.asset.url)) || 3;
      setResult({ asset: r.asset, durSec: dur });
      showToast?.("success", "Sprachausgabe erzeugt.");
    } catch (e) {
      showToast?.("error", `TTS fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  return (
    <details className="el-section">
      <summary>🗣 Text vorlesen (TTS)</summary>
      <textarea
        className="el-textarea"
        rows={3}
        placeholder="Text, der vorgelesen werden soll…"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="el-row">
        <span className="muted" style={{ fontSize: 11 }}>~{words} Wörter · ≈ {(words / 2.5).toFixed(1)}s</span>
        <button type="button" className="btn primary sm" disabled={!text.trim() || !voiceId || busy} onClick={() => void generate()}>
          {busy ? "Erzeuge…" : "▶ Generieren"}
        </button>
      </div>
      {result && (
        <ResultPreview asset={result.asset}>
          <button
            type="button"
            className="btn primary sm"
            onClick={() => {
              onPlaceVoiceover(result.asset, Math.max(0, playheadSec), result.durSec);
              showToast?.("success", "Am Playhead als Voiceover platziert.");
            }}
          >
            ✓ Am Playhead übernehmen
          </button>
        </ResultPreview>
      )}
    </details>
  );
};

const MusicSection: React.FC<{
  totalSec: number;
  onSetMusic: (asset: AudioAsset) => void;
  showToast?: ToastFn;
}> = ({ totalSec, onSetMusic, showToast }) => {
  const [prompt, setPrompt] = useState("");
  const [lengthSec, setLengthSec] = useState(() => Math.max(3, Math.min(300, Math.round(totalSec))));
  const [instrumental, setInstrumental] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AudioAsset | null>(null);

  const generate = async (): Promise<void> => {
    if (!prompt.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.elevenMusic({
        prompt: prompt.trim(),
        musicLengthMs: lengthSec * 1000,
        forceInstrumental: instrumental,
      });
      setResult(r.asset);
      showToast?.("success", "Musik erzeugt.");
    } catch (e) {
      showToast?.("error", `Musik-Generierung fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="el-section">
      <summary>🎵 Musik erzeugen</summary>
      <p className="muted el-hint">Benötigt einen bezahlten ElevenLabs-Plan. Länge ist auf die Reel-Dauer vorbelegt.</p>
      <textarea
        className="el-textarea"
        rows={2}
        placeholder={'z. B. treibender, moderner Synth-Beat, energiegeladen, 120 BPM …'}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
      />
      <label className="el-range">
        <span>Länge: {lengthSec}s</span>
        <input type="range" min={3} max={300} value={lengthSec} onChange={(e) => setLengthSec(Number(e.target.value))} />
      </label>
      <label className="el-check">
        <input type="checkbox" checked={instrumental} onChange={(e) => setInstrumental(e.target.checked)} />
        <span>Instrumental (keine Vocals)</span>
      </label>
      <div className="el-row">
        <span className="muted" style={{ fontSize: 11 }}>Reel: {totalSec.toFixed(1)}s</span>
        <button type="button" className="btn primary sm" disabled={!prompt.trim() || busy} onClick={() => void generate()}>
          {busy ? "Komponiere…" : "▶ Generieren"}
        </button>
      </div>
      {result && (
        <ResultPreview asset={result}>
          <button
            type="button"
            className="btn primary sm"
            onClick={() => {
              onSetMusic(result);
              showToast?.("success", "Als Musik-Spur gesetzt.");
            }}
          >
            ✓ Als Musik setzen
          </button>
        </ResultPreview>
      )}
    </details>
  );
};

const SfxSection: React.FC<{
  playheadSec: number;
  onAddSfx: (asset: AudioAsset, fromSec: number) => void;
  showToast?: ToastFn;
}> = ({ playheadSec, onAddSfx, showToast }) => {
  const [text, setText] = useState("");
  const [durationSeconds, setDurationSeconds] = useState(2);
  const [loop, setLoop] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AudioAsset | null>(null);

  const generate = async (): Promise<void> => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.elevenSfx({ text: text.trim(), durationSeconds, loop });
      setResult(r.asset);
      showToast?.("success", "Sound-Effekt erzeugt.");
    } catch (e) {
      showToast?.("error", `SFX-Generierung fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="el-section">
      <summary>🔊 Sound-Effekt erzeugen</summary>
      <textarea
        className="el-textarea"
        rows={2}
        placeholder={'z. B. kurzer cinematischer Whoosh · UI-Click · Regen im Wald …'}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <label className="el-range">
        <span>Dauer: {durationSeconds.toFixed(1)}s</span>
        <input type="range" min={0.5} max={30} step={0.5} value={durationSeconds} onChange={(e) => setDurationSeconds(Number(e.target.value))} />
      </label>
      <label className="el-check">
        <input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} />
        <span>Nahtlos loopbar</span>
      </label>
      <div className="el-row">
        <span className="muted" style={{ fontSize: 11 }}>Playhead: {playheadSec.toFixed(1)}s</span>
        <button type="button" className="btn primary sm" disabled={!text.trim() || busy} onClick={() => void generate()}>
          {busy ? "Erzeuge…" : "▶ Generieren"}
        </button>
      </div>
      {result && (
        <ResultPreview asset={result}>
          <button
            type="button"
            className="btn primary sm"
            onClick={() => {
              onAddSfx(result, Math.max(0, playheadSec));
              showToast?.("success", "Am Playhead als SFX platziert.");
            }}
          >
            ✓ Am Playhead übernehmen
          </button>
        </ResultPreview>
      )}
    </details>
  );
};

const AutoScoreSection: React.FC<{
  voiceId: string | null;
  locale: string;
  scenes: SceneLike[];
  sceneTimings: Map<string, { fromSec: number; durSec: number }>;
  onApplyAutoScore: (
    segments: { sceneUid: string; fromSec: number; durSec: number; src: string; assetId: string; label: string }[],
  ) => void;
  showToast?: ToastFn;
}> = ({ voiceId, locale, scenes, sceneTimings, onApplyAutoScore, showToast }) => {
  const [mode, setMode] = useState<"scene-text" | "ai-script">("scene-text");
  const [busy, setBusy] = useState(false);

  const run = async (): Promise<void> => {
    if (!voiceId || busy) return;
    setBusy(true);
    try {
      const payload = scenes.map((s) => {
        const t = sceneTimings.get(s.uid) ?? { fromSec: s.fromSec ?? 0, durSec: s.durationSec };
        return { uid: s.uid, text: extractSceneText(s), fromSec: t.fromSec, durSec: t.durSec };
      });
      const r = await api.elevenAutoScore({ scenes: payload, mode, voiceId, locale });
      // Echte Audiolängen proben → durSec exakt, nicht abgeschnitten.
      const segments = await Promise.all(
        r.segments.map(async (seg: AutoScoreSegment) => {
          const probed = await probeDuration(seg.src);
          return {
            sceneUid: seg.sceneUid,
            fromSec: seg.fromSec,
            durSec: probed > 0 ? probed : seg.sceneDurSec,
            src: seg.src,
            assetId: seg.assetId,
            label: seg.label,
          };
        }),
      );
      onApplyAutoScore(segments);
      showToast?.("success", `${segments.length} Szene(n) automatisch vertont.`);
    } catch (e) {
      showToast?.("error", `Auto-Vertonung fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="el-section">
      <summary>✨ Auto-Vertonung (ganzes Reel)</summary>
      <p className="muted el-hint">
        Erzeugt pro Szene ein Voiceover, zeitlich passend zur Szenenlänge. Ersetzt die
        aktuelle Voiceover-Spur.
      </p>
      <label className="el-radio">
        <input type="radio" checked={mode === "scene-text"} onChange={() => setMode("scene-text")} />
        <span>Szenentext vorlesen (was im Bild steht)</span>
      </label>
      <label className="el-radio">
        <input type="radio" checked={mode === "ai-script"} onChange={() => setMode("ai-script")} />
        <span>KI schreibt passenden Werbetext (braucht OpenAI-Key)</span>
      </label>
      <button
        type="button"
        className="btn primary"
        style={{ marginTop: 10, width: "100%" }}
        disabled={!voiceId || busy || scenes.length === 0}
        onClick={() => void run()}
      >
        {busy ? "Vertone…" : "✨ Reel automatisch vertonen"}
      </button>
    </details>
  );
};
